import get from "lodash-es/get";
import set from "lodash-es/set";
import cloneDeep from "lodash-es/cloneDeep";
import { format as formatDate, parse as parseDate, isValid } from "date-fns";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const TEMPLATE_VERSIONS = {
  V_0_1: "0.1",
  V_0_1_PREVIEW: "0.1-preview",
  V_1_0: "1.0"
};

function getTemplateVersion(templateSnapshot) {
  const schema = templateSnapshot?.$schema;

  if (!schema) {
    throw new Error("Template snapshot schema is required");
  }

  const match = schema.match(/statements\/([^/]+)\/schema\.json$/);

  if (!match) {
    throw new Error(`Invalid template schema: ${schema}`);
  }

  return match[1];
}

function normalizeField(field) {
  if (!field || typeof field !== "object") {
    throw new Error("Invalid statement field");
  }

  const normalizedField = {
    id: field.id,
    type: field.type,
    label: field.label,
    title: field.title,
    properties: {
      ...field.properties
    }
  };

  if (Array.isArray(field.fields)) {
    normalizedField.fields = field.fields.map(normalizeField);
  }

  return normalizedField;
}

function normalizeTemplateV01(templateSnapshot) {
  return {
    id: templateSnapshot.id,
    textTemplate: templateSnapshot.textTemplate ?? "",
    pages: [
      {
        id: "default",
        title: null,
        fields: (templateSnapshot.fields ?? []).map(normalizeField)
      }
    ]
  };
}

function normalizeTemplateV10(templateSnapshot) {
  const topLevelPages = (templateSnapshot.fields ?? []).filter(
    field => field.type === "page"
  );

  // Defensive fallback for a 1.0 template with no top-level "page" fields:
  // behave like 0.1 (single unnamed page).
  if (topLevelPages.length === 0) {
    return normalizeTemplateV01(templateSnapshot);
  }

  return {
    id: templateSnapshot.id,
    textTemplate: templateSnapshot.textTemplate ?? "",
    pages: topLevelPages.map(page => ({
      id: page.id,
      title: page.title ?? page.label ?? null,
      fields: (page.fields ?? []).map(normalizeField)
    }))
  };
}

const TEMPLATE_NORMALIZERS = {
  [TEMPLATE_VERSIONS.V_0_1]: normalizeTemplateV01,
  [TEMPLATE_VERSIONS.V_0_1_PREVIEW]: normalizeTemplateV01,
  [TEMPLATE_VERSIONS.V_1_0]: normalizeTemplateV10
};

function normalizeTemplateSnapshot(templateSnapshot) {
  if (!templateSnapshot || typeof templateSnapshot !== "object") {
    throw new Error("Template snapshot is required");
  }

  const version = getTemplateVersion(templateSnapshot);
  const normalizer = TEMPLATE_NORMALIZERS[version];

  if (!normalizer) {
    throw new Error(`Unsupported template version: ${version}`);
  }

  return normalizer(templateSnapshot);
}

const FIELD_TYPE_MAP = {
  date: {
    type: "date",
    enableFuture: true
  },
  dateTime: {
    type: "dateTime",
    enableFuture: true
  },
  time: {
    type: "time",
    enableFuture: true
  },
  text: {
    type: "staticText"
  },
  emailAddress: {
    type: "text"
  },
  select: {
    type: "select",
    literalOptionText: true
  },
  boolean: {
    type: "boolean"
  },
  longform: {
    type: "text",
    multiline: true
  },
  number: {
    type: "text",
    isNumber: true
  },
  phoneNumber: {
    type: "text",
    isPhoneNumber: true
  }
};

function parseOptions(options) {
  if (!Array.isArray(options)) return null;

  return options
    .filter(opt => opt && typeof opt === "object")
    .map(({ value, title }) => ({
      value,
      text: title
    }));
}

function mapFieldToSchema({ label, title, type, properties }, fieldValidation) {
  const base = FIELD_TYPE_MAP[type] ?? {
    type: "text"
  };

  return {
    ...base,
    label,
    title,
    properties: {
      ...properties,
      options: parseOptions(properties?.options)
    },
    validation: fieldValidation
  };
}

function getStatementSchema({ templateSnapshot, validationSchema }) {
  const normalized = normalizeTemplateSnapshot(templateSnapshot);
  const properties = {};
  const required = [];

  function walkFields(
    fields,
    parentPath,
    validationProperties,
    groupTitle,
    fieldPaths
  ) {
    fields.forEach(field => {
      if (field.type === "page" && Array.isArray(field.fields)) {
        walkFields(
          field.fields,
          parentPath,
          validationProperties,
          groupTitle,
          fieldPaths
        );
        return;
      }

      const path = parentPath ? `${parentPath}.${field.id}` : field.id;
      const fieldValidation = validationProperties?.[field.id];

      if (field.type === "group" && Array.isArray(field.fields)) {
        walkFields(
          field.fields,
          path,
          fieldValidation?.properties,
          field.title ?? field.label ?? null,
          fieldPaths
        );
        return;
      }

      properties[path] = { ...mapFieldToSchema(field, fieldValidation), groupTitle };
      fieldPaths.push(path);

      if (field.properties?.required) {
        required.push(path);
      }
    });
  }

  const pages = normalized.pages.map(page => {
    const fieldPaths = [];
    walkFields(page.fields, "", validationSchema.properties, null, fieldPaths);
    return { id: page.id, title: page.title ?? null, fieldPaths };
  });

  return {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    $id: normalized.id,
    type: "object",
    properties,
    ...(required.length > 0 && { required }),
    pages,
    StatementText: normalized.textTemplate
  };
}

// The draft PATCH endpoint doesn't enforce `required`, but a value that IS
// sent still gets format-validated and 400s if it doesn't match its pattern.
// Since a page mid-edit (e.g. a phone/email field not yet fully typed) is the
// common case for an autosave, fields that don't currently satisfy their own
// pattern are left out of the merge patch entirely rather than sent invalid -
// omitting a field from a JSON Merge Patch just leaves it untouched server-side.
// An empty value is exempt from this check: clearing a field is a deliberate
// edit, not a mid-typing invalid state, so it must still reach the server -
// otherwise the previously-saved value would be stuck there forever. Required-
// ness and pattern strictness are still fully enforced elsewhere, for page
// navigation (FormContent) and the final submit (StatementForm's Yup schema).
function isDraftSafeValue(field, value) {
  const pattern = field.validation?.pattern;
  if (!pattern || typeof value !== "string" || value === "") return true;
  return new RegExp(pattern).test(value);
}

// date/dateTime/time fields hold a native Date object while being edited
// (see CustomField's date-io pickers). Letting that reach JSON.stringify
// directly would implicitly convert it to a UTC "Z" string via
// Date.prototype.toJSON(), which the backend then interprets in a different
// timezone than what the user actually entered on screen. Formatting
// explicitly as a naive local string (no offset, no "Z") removes timezone
// conversion from the equation entirely, matching the pattern already used
// for incident dates (see src/utils/incidents.js).
//
const DATE_FORMATS = {
  date: "yyyy-MM-dd",
  dateTime: "yyyy-MM-dd'T'HH:mm:ss",
  time: "HH:mm:ss"
};

// An unselected date/dateTime/time field holds null in Formik/the picker,
// but the backend rejects a JSON null for these ("Invalid type. Expected
// String but got Null.") - it wants an empty string instead.
function formatDateValue(field, value) {
  const dateFormat = DATE_FORMATS[field.type];
  if (!dateFormat) return value;
  if (value == null) return "";
  if (!(value instanceof Date)) return value;

  return formatDate(value, dateFormat);
}

// Normalizes a phone field to E.164 before submit - the backend rejects
// anything else ("Invalid phone number format (expected E.164)"). Falls back
// to "GB" for a bare national number, matching this codebase's established
// phone-country convention (see src/utils/validatePhoneNumber.js).
function formatPhoneValue(field, value) {
  if (!field.isPhoneNumber || !value) return value;
  const phone = parsePhoneNumberFromString(value, "GB");
  return phone?.isValid() ? phone.number : value;
}

function formatFieldValue(field, value) {
  return formatPhoneValue(field, formatDateValue(field, value));
}

// Mirror of formatDateValue for the read side: saved values come back as the
// same naive strings we now send, and parsing them with date-fns (rather
// than handing the raw string to the picker to parse as a native Date)
// avoids the native Date parser's UTC-midnight quirk for date-only strings.
// An empty string (the wire representation of "unselected") is converted
// back to null, matching what the picker/getDefaultValue expect internally.
function parseFieldValue(field, value) {
  const dateFormat = DATE_FORMATS[field.type];
  if (!dateFormat) return value;
  if (value === "") return null;
  if (typeof value !== "string") return value;

  const parsed = parseDate(value, dateFormat, new Date());
  return isValid(parsed) ? parsed : value;
}

// "" is Formik's default for an untouched field, but a genuine type
// violation for a non-string one (e.g. a number). null removes the key
// instead (RFC 7396 merge patch) - correct whether never answered or
// deliberately cleared.
function isEmptyNonString(field, value) {
  const validationType = field.validation?.type;
  return value === "" && !!validationType && validationType !== "string";
}

function getDraftResponses(schema, values) {
  const responses = {};

  Object.entries(schema.properties).forEach(([path, field]) => {
    if (field.type === "staticText") return;

    const value = get(values, path);
    if (value === undefined || !isDraftSafeValue(field, value)) return;

    set(
      responses,
      path,
      isEmptyNonString(field, value) ? null : formatFieldValue(field, value)
    );
  });

  return responses;
}

// Used for the final submit, which - unlike getDraftResponses - sends the
// complete values tree as-is (already validated/required-checked via Yup),
// so this only reformats date/phone fields without omitting anything.
function formatDateFields(schema, values) {
  const result = cloneDeep(values);

  Object.entries(schema.properties).forEach(([path, field]) => {
    if (field.type === "staticText") return;
    set(result, path, formatFieldValue(field, get(values, path)));
  });

  return result;
}

export default {
  getTemplateVersion,
  normalizeTemplateSnapshot,
  getStatementSchema,
  getDraftResponses,
  formatDateFields,
  parseFieldValue
};
